/**
 * Canonical evaluation behavior catalogs, one per client. Definitions live in `behaviors/shared.ts`; each
 * client's list in `behaviors/<client>.ts` picks from them, and its array order is that client's call-flow order.
 * Consumed by training-profile-engine (Call Focus / evaluation criteria) and expert-ui.
 */

import type { BehaviorDefinition, BehaviorCategoryId, BehaviorCriteria, BehaviorCriterion } from "./behaviors/types";

import { AMAZONROUTER_BEHAVIORS } from "./behaviors/amazonrouter";
import { ATT_BEHAVIORS } from "./behaviors/att";
import { VERIZON_BEHAVIORS } from "./behaviors/verizon";

export type {
    BehaviorDefinition,
    BehaviorCategoryId,
    BehaviorCriteria,
    BehaviorCriterion,
    MatchMode,
} from "./behaviors/types";

export const BEHAVIOR_CATEGORY_IDS: readonly BehaviorCategoryId[] = [
    "serveAndSolve",
    "sellSmartOffer",
    "sellClosing",
    "other",
];

export function isBehaviorCategoryId(value: unknown): value is BehaviorCategoryId {
    return typeof value === "string" && (BEHAVIOR_CATEGORY_IDS as readonly string[]).includes(value);
}

/** Display labels for Call Focus grouping. */
export const BEHAVIOR_CATEGORY_OPTIONS: readonly { id: BehaviorCategoryId; label: string }[] = [
    { id: "serveAndSolve", label: "Serve & Solve" },
    { id: "sellSmartOffer", label: "Sell - Smart Offer" },
    { id: "sellClosing", label: "Sell - Closing" },
    { id: "other", label: "Other Behaviors" },
];

/** Deep copy so a profile can hold its own wording without aliasing the shared defaults. */
export function cloneCriterion(criterion: BehaviorCriterion): BehaviorCriterion {
    return {
        ...criterion,
        failIf: [...criterion.failIf],
        passIf: [...criterion.passIf],
        ...(criterion.requiredPhrases ? { requiredPhrases: [...criterion.requiredPhrases] } : {}),
    };
}

export type BehaviorCatalogPartner = "verizon" | "att" | "amazonrouter";

export type BehaviorCategory = {
    id: BehaviorCategoryId;
    label: string;
    behaviorIds: readonly string[];
};

export type BehaviorCatalog = {
    /** Catalog entries in call-flow order. */
    behaviors: readonly BehaviorDefinition[];
    /** Behavior ids in call-flow order. */
    callOrder: readonly string[];
    /** Evaluation criteria defaults for profiles (catalog-only `category` stripped). */
    criteria: BehaviorCriterion[];
    defaultIds: ReadonlySet<string>;
    /** Call Focus display groups, call-flow order within each group. */
    categories: readonly BehaviorCategory[];
};

function buildBehaviorCatalog(behaviors: readonly BehaviorDefinition[]): BehaviorCatalog {
    const callOrder = behaviors.map((entry) => entry.behavior);
    return {
        behaviors,
        callOrder,
        criteria: behaviors.map(({ category: _category, ...criterion }) => criterion),
        defaultIds: new Set(callOrder),
        categories: BEHAVIOR_CATEGORY_OPTIONS.filter((option) => option.id !== "other").map((option) => ({
            id: option.id,
            label: option.label,
            behaviorIds: behaviors.filter((entry) => entry.category === option.id).map((entry) => entry.behavior),
        })),
    };
}

export const BEHAVIOR_CATALOGS: Readonly<Record<BehaviorCatalogPartner, BehaviorCatalog>> = {
    verizon: buildBehaviorCatalog(VERIZON_BEHAVIORS),
    att: buildBehaviorCatalog(ATT_BEHAVIORS),
    amazonrouter: buildBehaviorCatalog(AMAZONROUTER_BEHAVIORS),
};

// The exports below are the Verizon catalog (built for the Ascend pilot), kept under their original names for
// existing consumers.
// Prefer BEHAVIOR_CATALOGS[partner] in new code.

export const ASCEND_BEHAVIORS: readonly BehaviorDefinition[] = BEHAVIOR_CATALOGS.verizon.behaviors;

/** Call-flow order of Ascend behavior ids (derived from {@link ASCEND_BEHAVIORS}). */
export const ASCEND_BEHAVIOR_CALL_ORDER: readonly string[] = BEHAVIOR_CATALOGS.verizon.callOrder;

/** Evaluation criteria defaults for profiles (strips catalog-only {@link BehaviorDefinition} fields). */
export const BEHAVIORS: BehaviorCriteria = { criteria: BEHAVIOR_CATALOGS.verizon.criteria };

export const DEFAULT_BEHAVIOR_IDS: ReadonlySet<string> = BEHAVIOR_CATALOGS.verizon.defaultIds;

/** Call Focus display groups derived from catalog {@link BehaviorDefinition.category}. */
export const ASCEND_BEHAVIOR_CATEGORIES: readonly BehaviorCategory[] = BEHAVIOR_CATALOGS.verizon.categories;
